import { type RefObject, useEffect } from 'react';

/** Whether this browser can paint ranges without touching the DOM. */
export function highlightsSupported(): boolean {
  return typeof CSS !== 'undefined' && 'highlights' in CSS;
}

/**
 * Keep the range an open "add to chat" popover is about visibly selected.
 *
 * The popover autofocuses its note field, and focusing a textarea collapses the document's
 * selection — so the highlight the user just made vanishes at the exact moment the panel asking
 * about it appears, leaving them to write a note about something they can no longer see.
 * Repainting the same range through the CSS Custom Highlight API survives the focus change,
 * because it is not the browser's selection. Where the API is missing the range is simply not
 * repainted — the old blink-out behaviour, nothing worse.
 *
 * `name` is the `::highlight()` name to paint under; each pane gets its own so two popovers open
 * in the same view (plan pane and thread) can't clobber each other's range.
 */
export function usePendingRangeHighlight(
  rangeRef: RefObject<Range | null>,
  /** The popup state object — a new identity per popup, so the range repaints per selection. */
  popup: unknown,
  name: string,
) {
  useEffect(() => {
    if (!highlightsSupported()) return;
    const range = rangeRef.current;
    if (!popup || !range) {
      CSS.highlights.delete(name);
      return;
    }
    const highlight = new Highlight(range);
    // Above any "already staged" highlight, so marking something twice shows the live selection
    // rather than the weaker shading underneath it.
    highlight.priority = 1;
    CSS.highlights.set(name, highlight);
    return () => {
      CSS.highlights.delete(name);
    };
  }, [rangeRef, popup, name]);
}
